// src/pages/EditCountry.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "../components/Navbar";

interface Country {
  id?: number; // Optional for new entries, but fetched entries have an id
  name: string;
  created_at?: string; // Used if you want to display a date later
  hidden?: boolean;
}

const EditCountry: React.FC = () => {
  const navigate = useNavigate();
  const [countries, setCountries] = useState<Country[]>([]);
  const [newCountry, setNewCountry] = useState<Country>({ name: "" });
  const [error, setError] = useState("");
  const [showPopup, setShowPopup] = useState(false);

  // State for deletion confirmation
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [countryToDelete, setCountryToDelete] = useState<Country | null>(null);
  const [confirmText, setConfirmText] = useState("");

  // Fetch all countries on mount (backend should return only non-hidden countries)
  useEffect(() => {
    fetch("/api/countries")
      .then((res) => res.json())
      .then((data) => setCountries(data))
      .catch((err) => setError("Error fetching countries"));
  }, []);

  // Listen for any click to dismiss the "Updated" popup when it's shown
  useEffect(() => {
    if (showPopup) {
      const handleDocumentClick = () => setShowPopup(false);
      document.addEventListener("click", handleDocumentClick);
      return () => document.removeEventListener("click", handleDocumentClick);
    }
  }, [showPopup]);

  // Update a specific country's name in state
  const handleCountryChange = (id: number, value: string) => {
    setCountries((prev) =>
      prev.map((country) =>
        country.id === id ? { ...country, name: value } : country
      )
    );
  };

  // Update the new country row
  const handleNewCountryChange = (value: string) => {
    setNewCountry({ name: value });
  };

  // Save an existing country via PUT using the expected payload format
  const handleUpdate = async (country: Country) => {
    if (!country.id) return;
    try {
      const response = await fetch(`/api/countries/${country.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ countryName: country.name, hidden: country.hidden || false }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Error updating country");
      }
    } catch (err) {
      setError("Error connecting to the server");
    }
  };

  // Save the new country via POST
  const handleNewSave = async () => {
    if (newCountry.name.trim() === "") return;
    try {
      const response = await fetch("/api/countries", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // API expects { countryName: string }
        body: JSON.stringify({ countryName: newCountry.name }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Error adding country");
      } else {
        const createdCountry = await response.json();
        setCountries((prev) => [createdCountry, ...prev]);
        setNewCountry({ name: "" });
        setError("");
      }
    } catch (err) {
      setError("Error connecting to the server");
    }
  };

  // Delete (hide) a country by updating its hidden flag to true
  const handleDeleteCountry = async (country: Country) => {
    if (!country.id) return;
    try {
      const response = await fetch(`/api/countries/${country.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ countryName: country.name, hidden: true }),
      });
      if (!response.ok) {
        const errorData = await response.json();
        setError(errorData.error || "Error hiding country");
      } else {
        // Remove the country from local state after successful update
        setCountries((prev) => prev.filter((c) => c.id !== country.id));
        setShowDeleteModal(false);
        setCountryToDelete(null);
        setConfirmText("");
      }
    } catch (err) {
      setError("Error connecting to the server");
    }
  };

  // Open the delete confirmation modal for a specific country
  const openDeleteModal = (country: Country) => {
    setCountryToDelete(country);
    setConfirmText("");
    setShowDeleteModal(true);
  };

  // Update all countries and add new one if present, then show the "Updated" popup
  const handleUpdateAll = async () => {
    if (newCountry.name.trim() !== "") {
      await handleNewSave();
    }
    for (const country of countries) {
      if (country.name.trim() !== "") {
        await handleUpdate(country);
      }
    }
    setShowPopup(true);
  };

  // Styles
  const containerStyle: React.CSSProperties = {
    backgroundColor: "#F0F5F0",
    minHeight: "100vh",
    padding: "20px",
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: "#FFFFFF",
    borderRadius: "8px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.1)",
    maxWidth: "600px",
    margin: "50px auto",
    padding: "30px",
  };

  const headerStyle: React.CSSProperties = {
    textAlign: "center",
    color: "#e41e26",
    borderBottom: "2px solid #e41e26",
    paddingBottom: "10px",
    marginBottom: "20px",
    fontSize: "24px",
  };

  const tableStyle: React.CSSProperties = {
    width: "100%",
    borderCollapse: "collapse",
  };

  const thStyle: React.CSSProperties = {
    textAlign: "left",
    padding: "8px",
    borderBottom: "1px solid #ccc",
    color: "#e41e26",
  };

  const tdStyle: React.CSSProperties = {
    padding: "8px",
    borderBottom: "1px solid #ccc",
  };

  const inputStyle: React.CSSProperties = {
    width: "100%",
    padding: "5px",
    boxSizing: "border-box",
  };

  const buttonStyle: React.CSSProperties = {
    padding: "10px 20px",
    backgroundColor: "#e41e26",
    color: "#fff",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",
    fontSize: "16px",
    marginRight: "10px",
  };

  const navContainerStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "20px",
  };

  const popupStyle: React.CSSProperties = {
    position: "fixed",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    backgroundColor: "#e41e26",
    color: "#fff",
    padding: "15px 30px",
    borderRadius: "4px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.3)",
    zIndex: 1000,
  };

  const trashButtonStyle: React.CSSProperties = {
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "18px",
  };

  // Modal overlay styles for delete confirmation
  const modalOverlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 1000,
  };

  // Modal content styles for delete confirmation
  const modalContentStyle: React.CSSProperties = {
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "8px",
    maxWidth: "400px",
    width: "90%",
    textAlign: "center",
  };

  return (
    <div style={containerStyle}>
      <Navbar />
      {showPopup && (
        <div style={popupStyle} onClick={() => setShowPopup(false)}>
          Updated
        </div>
      )}
      <div style={cardStyle}>
        <h1 style={headerStyle}>Edit Country</h1>
        {error && <p style={{ color: "red", textAlign: "center" }}>{error}</p>}
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Country</th>
              <th style={thStyle}></th>
            </tr>
          </thead>
          <tbody>
            {countries.map((country) => (
              <tr key={country.id}>
                <td style={tdStyle}>
                  <input
                    type="text"
                    value={country.name}
                    onChange={(e) => handleCountryChange(country.id!, e.target.value)}
                    style={inputStyle}
                  />
                </td>
                <td style={tdStyle}>
                  <button style={trashButtonStyle} onClick={() => openDeleteModal(country)}>
                    🗑️
                  </button>
                </td>
              </tr>
            ))}
            {/* New country entry row */}
            <tr>
              <td style={tdStyle}>
                <input
                  type="text"
                  value={newCountry.name}
                  onChange={(e) => handleNewCountryChange(e.target.value)}
                  style={inputStyle}
                  placeholder="New Country"
                />
              </td>
              <td style={tdStyle}></td>
            </tr>
          </tbody>
        </table>
        <div style={navContainerStyle}>
          <button style={buttonStyle} onClick={() => navigate(-1)}>
            Back
          </button>
          <button style={buttonStyle} onClick={handleUpdateAll}>
            Update
          </button>
        </div>
      </div>
      {/* Delete Confirmation Modal */}
      {showDeleteModal && countryToDelete && (
        <div style={modalOverlayStyle}>
          <div style={modalContentStyle}>
            <h3>Confirm Deletion</h3>
            <p>
              Are you sure you want to delete <strong>{countryToDelete.name}</strong>?<br />
              Type <strong>DELETE</strong> to confirm.
            </p>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type DELETE to confirm"
              style={{ width: "80%", padding: "8px", margin: "10px 0" }}
            />
            <div style={{ marginTop: "10px" }}>
              <button
                style={buttonStyle}
                onClick={() => {
                  if (confirmText === "DELETE") {
                    handleDeleteCountry(countryToDelete);
                  } else {
                    alert("Please type DELETE to confirm.");
                  }
                }}
              >
                Submit
              </button>
              <button style={{ ...buttonStyle, marginLeft: "10px" }} onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditCountry;
